/*
 * Used to expose an api that can be used to call the CallGuide Web SDK.
 */
import { RequestCallbackActions } from '../actions/callguideRequestCallbackActions';
import { ConfigState } from '../redux/slices/config';
import { ChatArchive } from '../types/async-config';
import { CallGuideChatApi, ContactData } from '../types/call-guide/chat-api';
import { CallGuideEventCallbacks } from '../types/call-guide/events';
import { chatSession } from '../utils/session';

export class CallGuideService {
  eventCallbacks: CallGuideEventCallbacks;
  requestCallbacks: RequestCallbackActions;
  windowObj: Window;
  callguideApi?: CallGuideChatApi;
  chatArchives: ChatArchive[];

  /**
   * Initialize service with all needed dependencies, must be called before use.
   *
   * @param eventCallbacks callguide events
   * @param requestCallbacks callguide events
   * @param config the application JSON config
   * @param callguideApi to be able to override in tests
   * @param windowObj to be able to override in tests
   */
  init(
    eventCallbacks: CallGuideEventCallbacks,
    requestCallbacks: RequestCallbackActions,
    config: ConfigState,
    callguideApi: CallGuideChatApi | undefined = window.CallGuide?.api.chat,
    windowObj = window
  ) {
    this.eventCallbacks = eventCallbacks;
    this.requestCallbacks = requestCallbacks;
    this.windowObj = windowObj;
    this.callguideApi = callguideApi;

    this.chatArchives = config?.chatArchives || [];
    this.callguideApi?.initialize(
      eventCallbacks,
      requestCallbacks,
      '#pen-status-selector'
    );
  }

  findArchive(entrance: string, errand: string) {
    // check if we can find entrance with errand which has priority.
    let archiveSection = this.chatArchives.find(
      (q) => q.entrance === entrance && q.errand === errand
    );

    // check if we can find mapping for entrance.
    if (!archiveSection) {
      archiveSection = this.chatArchives.find(
        (q) => q.entrance === entrance && !q.errand
      );
    }

    // if still not found, use default values.
    if (!archiveSection) {
      archiveSection = {
        gf_companyId: 'gf',
        gf_archiveSectionId: 'GFGeneral',
        gf_country: 'NO'
      };
    }

    return archiveSection;
  }

  write(message: string) {
    this.callguideApi?.write(message);
  }

  deleteSession() {
    chatSession.clearSavedState();

    this.callguideApi?.delete();
    this.callguideApi?.initialize(
      this.eventCallbacks,
      this.requestCallbacks,
      '#pen-status-selector'
    );
  }

  endChat() {
    chatSession.clearSavedState();

    this.callguideApi?.quit();
  }

  penStatusOn() {
    this.callguideApi?.penStatusOn();
  }

  requestChat(
    entrance: string,
    errand: string,
    initialMessage: string,
    contactData: ContactData
  ) {
    const [topic] = this.windowObj.location.pathname.split('/').slice(-1);
    const gfTopic = topic === '' ? 'Forside' : topic;
    const archiveSection = this.findArchive(entrance, errand);

    const expandedContactData = {
      ...contactData,
      gf_topic: gfTopic,
      gf_country: archiveSection.gf_country,
      gf_archiveSectionId: archiveSection.gf_archiveSectionId,
      gf_companyId: archiveSection.gf_companyId
    };
    this.callguideApi?.request(
      entrance,
      errand,
      initialMessage,
      expandedContactData
    );
  }
}

const callGuideService = new CallGuideService();
export default callGuideService;
